<template>
  <div class="cfin">
    <page-skeleton v-if="loading" variant="finance" class="mt-4" />

    <template v-else>
      <p class="cfin__eyebrow">{{ $t("customerFinance.eyebrow") }}</p>

      <!-- herói: o que você deve + Pitch Coins -->
      <div class="cfin-hero">
        <div class="cfin-hero__charge">
          <span class="cfin-tag">{{ $t("customerFinance.nextCharge") }}</span>

          <template v-if="nextPendingCharge">
            <p class="cfin-money cfin-display">
              <span class="cfin-money__sym">R$</span
              ><span class="cfin-money__int">{{
                moneyParts(nextPendingCharge.amount).int
              }}</span
              ><span class="cfin-money__dec"
                >{{ $t("formats.decimalSep")
                }}{{ moneyParts(nextPendingCharge.amount).dec }}</span
              >
            </p>
            <p class="cfin-hero__meta">
              {{ chargeHeroMeta(nextPendingCharge) }}
            </p>
            <charge-payment-box :charge="nextPendingCharge" />
          </template>

          <template v-else>
            <p class="cfin-money cfin-display cfin-money--clear">
              {{ $t("customerFinance.allClear") }}
            </p>
            <p class="cfin-hero__meta">
              {{ $t("customerFinance.noOpen") }}
            </p>
          </template>

          <p class="cfin-method">
            <i class="mdi" :class="paymentMethodIcon" aria-hidden="true"></i>
            {{ $t("customerFinance.youPayWith") }}
            <strong>{{ paymentMethodName }}</strong>
            <router-link class="cfin-method__link" :to="paymentMethodRoute">{{
              $t("customerFinance.change")
            }}</router-link>
          </p>
        </div>

        <!-- Card 86ak0nkuq: a mensalidade contratada aparece mesmo sem
             cobrança gerada. Antes o aluno solo lia "Tudo em dia" e concluía
             que o valor definido pelo administrativo não existia. -->
        <div v-if="finance.plan" class="cfin-plan">
          <span class="cfin-tag cfin-tag--light">{{
            $t("customerFinance.yourFee")
          }}</span>

          <p v-if="finance.plan.monthlyValue" class="cfin-plan__value">
            {{ formatCurrency(finance.plan.monthlyValue) }}
            <span class="cfin-plan__cycle">{{
              $t("customerFinance.perMonth")
            }}</span>
          </p>
          <p v-else class="cfin-plan__value cfin-plan__value--empty">
            {{ $t("customerFinance.tbd") }}
          </p>

          <p v-if="planBreakdown" class="cfin-plan__hint">
            {{ planBreakdown }}
          </p>
          <p v-if="finance.plan.pendingReason" class="cfin-plan__pending">
            {{ finance.plan.pendingReason }}
          </p>
        </div>

        <!-- Card 17trt6jd8uu: coins sao quantidade, nao dinheiro; o card
             leva ao extrato do Pitch Coins. -->
        <router-link to="/connect/aluno/pitch-coins" class="cfin-coins">
          <span class="cfin-coins__mark" aria-hidden="true">
            <i class="mdi mdi-hand-coin-outline"></i>
          </span>
          <span class="cfin-tag cfin-tag--light">Pitch Coins</span>
          <p class="cfin-coins__value cfin-display">
            {{ $t("pitchCoins.amountCoins", { amount: coinsAvailable }) }}
          </p>
          <p class="cfin-coins__hint">{{ $t("customerFinance.coinsHint") }}</p>
        </router-link>
      </div>

      <!-- livro de mensalidades -->
      <section class="cfin-ledger">
        <div class="cfin-ledger__head">
          <div>
            <p class="cfin-section">{{ $t("customerFinance.charges") }}</p>
            <p class="cfin-section__sub">
              <template v-if="finance.summary.pendingCount">
                {{
                  $tc(
                    "customerFinance.openSummary",
                    finance.summary.pendingCount,
                    {
                      amount: formatCurrency(
                        finance.summary.totalPendingAmount || 0
                      ),
                      count: finance.summary.pendingCount,
                    }
                  )
                }}
              </template>
              <template v-else>{{
                $t("customerFinance.nothingPending")
              }}</template>
            </p>
          </div>

          <div class="cfin-legend" aria-hidden="true">
            <span class="cfin-legend__i"
              ><i class="cfin-dot" style="--c: #00a379"></i
              >{{ $t("customerFinance.paid") }}</span
            >
            <span class="cfin-legend__i"
              ><i class="cfin-dot" style="--c: #ff5d2a"></i
              >{{ $t("customerFinance.pending") }}</span
            >
            <span class="cfin-legend__i"
              ><i class="cfin-dot" style="--c: #d52323"></i
              >{{ $t("customerFinance.overdue") }}</span
            >
          </div>
        </div>

        <div v-if="finance.items.length" class="cfin-list">
          <article
            v-for="charge in finance.items"
            :key="charge.id"
            class="cfin-row"
            :style="{ '--c': statusHex(charge.status) }"
          >
            <span class="cfin-row__stripe" aria-hidden="true"></span>
            <div class="cfin-row__info">
              <p class="cfin-row__title">
                {{ chargeMonthLabel(charge.month, charge.year) }}
              </p>
              <p class="cfin-row__meta">{{ chargeRowMeta(charge) }}</p>
            </div>
            <div class="cfin-row__right">
              <span class="cfin-row__amount">
                {{ formatCurrency(charge.amount) }}
                <!-- Card 17trt6jd8uu: desconto de resgate, ja descontado. -->
                <span
                  v-if="charge.coinDiscountAmount > 0"
                  class="cfin-row__coins"
                >
                  {{
                    $t("pitchCoins.chargeDiscount", {
                      amount: formatCurrency(charge.coinDiscountAmount),
                    })
                  }}
                </span>
              </span>
              <span class="cfin-status">{{ statusLabel(charge.status) }}</span>
            </div>
          </article>
        </div>

        <div v-else class="cfin-empty">
          {{ $t("customerFinance.empty") }}
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import ChargePaymentBox from "@/components/connect/customer/ChargePaymentBox.vue";
import { PAYMENT_METHODS, paymentMethodLabel } from "@/shared/paymentMethods";
import {
  chargeMonthLabel,
  chargeOriginParts,
} from "@/shared/chargePresentation";
import { formatBRL, languageTag } from "@/plugins/i18n";
import { formatCoins } from "@/shared/coins";

export default {
  name: "CustomerFinance",

  components: {
    ChargePaymentBox,
  },

  data() {
    return {
      loading: true,
      finance: {
        wallet: {
          cashBalance: 0,
          coinBalance: 0,
        },
        summary: {
          pendingCount: 0,
        },
        plan: null,
        preferredPaymentMethod: "credit_card",
        items: [],
        referenceLabel: "",
      },
      coins: null,
    };
  },

  async created() {
    await this.loadFinance();
  },

  computed: {
    // Saldo do extrato de coins; a wallet so serve de reserva se a leitura
    // falhar (ela e um cache e pode atrasar um vencimento).
    coinsAvailable() {
      return formatCoins(
        this.coins ? this.coins.available : this.finance.wallet.coinBalance
      );
    },
    paymentMethodName() {
      return paymentMethodLabel(this.finance.preferredPaymentMethod);
    },
    paymentMethodIcon() {
      const found = PAYMENT_METHODS.find(
        (item) => item.value === this.finance.preferredPaymentMethod
      );
      return found ? found.icon : "mdi-credit-card-outline";
    },
    // A escolha mora na aba Financeiro do perfil, junto do cartão.
    paymentMethodRoute() {
      return { path: "/connect/aluno/perfil", query: { aba: "tab-4" } };
    },
    // Quebra do valor: o aluno precisa reconhecer a mensalidade que combinou
    // e entender de onde vem a diferença para o total cobrado.
    planBreakdown() {
      const plan = this.finance.plan;

      if (!plan || !plan.monthlyValue || !plan.baseValue) {
        return "";
      }

      const parts = [];

      // Só faz sentido quebrar o valor quando ele é composto. No plano
      // Autonomy não há taxa (card 86ak0nkmr), então repetir "Plano R$ 79,90"
      // logo abaixo de "R$ 79,90/mês" seria ruído.
      if (plan.platformFee) {
        parts.push(
          this.$t("customerFinance.plan", {
            value: this.formatCurrency(plan.baseValue),
          }),
          this.$t("customerFinance.platformFee", {
            value: this.formatCurrency(plan.platformFee),
          })
        );
      }

      if (plan.teacherName) {
        parts.push(
          this.$t("customerFinance.withTeacher", { name: plan.teacherName })
        );
      }

      return parts.join(" · ");
    },
    nextPendingCharge() {
      return this.finance.items.find((item) => item.status !== "PAID") || null;
    },
  },

  methods: {
    chargeMonthLabel,
    // Linha do extrato: o mes vai no titulo; aqui ficam professor, turma e
    // vencimento (card 86ak0nkuq).
    chargeRowMeta(charge) {
      return [
        ...chargeOriginParts(charge),
        this.$t("customerFinance.due", { date: charge.dueDateLabel }),
        paymentMethodLabel(charge.paymentMethod),
      ]
        .filter(Boolean)
        .join(" · ");
    },
    chargeHeroMeta(charge) {
      return [
        chargeMonthLabel(charge.month, charge.year),
        this.$t("customerFinance.dueOn", { date: charge.dueDateLabel }),
        ...chargeOriginParts(charge),
      ]
        .filter(Boolean)
        .join(" · ");
    },
    async loadFinance() {
      this.loading = true;

      try {
        const [res, coins] = await Promise.all([
          customerApi.getFinance(),
          customerApi.getCoinsSummary().catch(() => null),
        ]);
        this.finance = {
          ...this.finance,
          ...res.data,
        };
        this.coins = coins?.data?.data || null;
      } catch (_error) {
        this.$root.$emit("display-alert", {
          text: this.$t("customerFinance.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      return formatBRL(value);
    },
    // Separa parte inteira e centavos para o tratamento tipográfico do herói
    moneyParts(value) {
      const abs = Math.abs(Number(value || 0)).toFixed(2);
      const [int, dec] = abs.split(".");
      return {
        int: Number(int).toLocaleString(languageTag()),
        dec,
      };
    },
    statusLabel(status) {
      if (status === "PAID") {
        return this.$t("customerFinance.paid");
      }

      if (status === "OVERDUE") {
        return this.$t("customerFinance.overdue");
      }

      return this.$t("customerFinance.pending");
    },
    statusHex(status) {
      if (status === "PAID") {
        return "#00a379";
      }

      if (status === "OVERDUE") {
        return "#d52323";
      }

      return "#ff5d2a";
    },
  },
};
</script>

<style scoped>
.cfin {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  width: 100%;
  padding: 8px 4px 40px;
  color: var(--ink);
  font-family: "Poppins", sans-serif;
  /* O layout responde a largura do conteudo, nao da tela: no desktop o menu
     lateral aberto come ~256px, e um breakpoint de viewport deixava a coluna
     do valor e do Pix espremida entre 960 e ~1250px. */
  container-type: inline-size;
  container-name: cfin;
}

.cfin-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.cfin__eyebrow {
  margin: 6px 0 14px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pink);
}

.cfin-tag {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}
.cfin-tag--light {
  color: rgba(255, 255, 255, 0.82);
}

/* ---------- herói ---------- */
.cfin-hero {
  display: grid;
  /* Trilhas `auto` colapsam quando o card não é renderizado, então o layout
     continua o mesmo para quem não tem plano contratado. */
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 18px;
  align-items: stretch;
}

.cfin-plan {
  min-width: 220px;
  padding: 22px 24px;
  border-radius: 22px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.1);
  background: var(--dk-surface, #fff);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cfin-plan__value {
  margin: 8px 0 0;
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--dk-ink, #231a2e);
  line-height: 1.1;
}

.cfin-plan__value--empty {
  color: var(--dk-muted, #6b6472);
  font-size: 1.15rem;
}

.cfin-plan__cycle {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--dk-muted, #6b6472);
}

.cfin-plan__hint {
  margin: 6px 0 0;
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6472);
}

.cfin-plan__pending {
  margin: 8px 0 0;
  font-size: 0.78rem;
  color: var(--dk-ember-deep, #b4560f);
}
.cfin-hero__charge {
  /* O código Pix é uma linha longa sem quebra: sem min-width 0 ele alarga a
     coluna do grid e empurra o card para fora da tela. */
  min-width: 0;
  padding: 26px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 140% at 100% 0%,
      rgba(232, 39, 111, 0.08),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.cfin-money {
  margin: 10px 0 0;
  color: var(--ink);
  line-height: 1;
  font-weight: 700;
}
.cfin-money__sym {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--muted);
  margin-right: 6px;
  vertical-align: 0.5em;
}
.cfin-money__int {
  font-size: clamp(2.6rem, 6vw, 3.6rem);
  font-size: clamp(2.2rem, 8cqi, 3.6rem);
}
.cfin-money__dec {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--muted);
}
.cfin-money--clear {
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: var(--dk-teal, #00a379);
}
.cfin-hero__meta {
  margin: 12px 0 0;
  font-size: 0.9rem;
  color: var(--muted);
}

.cfin-method {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 16px 0 0;
  padding-top: 14px;
  border-top: 1px solid var(--line);
  font-size: 0.82rem;
  color: var(--muted);
}
.cfin-method .mdi {
  font-size: 1.05rem;
  color: var(--pink);
}
.cfin-method strong {
  color: var(--ink);
  font-weight: 600;
}
.cfin-method__link {
  margin-left: 4px;
  font-weight: 600;
  color: var(--pink);
  text-decoration: none;
}
.cfin-method__link:hover,
.cfin-method__link:focus-visible {
  text-decoration: underline;
}

/* Pitch Coins */
.cfin-coins {
  position: relative;
  min-width: 240px;
  padding: 22px 24px;
  border-radius: 22px;
  background: linear-gradient(
    158deg,
    var(--dk-pink, #e8276f) 0%,
    var(--dk-grape, #a826e1) 100%
  );
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 20px 38px -22px rgba(168, 38, 225, 0.6);
}
a.cfin-coins {
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
a.cfin-coins:hover,
a.cfin-coins:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 24px 40px -22px rgba(168, 38, 225, 0.7);
}
a.cfin-coins:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -6px;
}
@media (prefers-reduced-motion: reduce) {
  a.cfin-coins {
    transition: none;
  }
  a.cfin-coins:hover {
    transform: none;
  }
}
.cfin-coins__mark {
  position: absolute;
  top: -18px;
  right: -14px;
  font-size: 116px;
  line-height: 1;
  opacity: 0.16;
}
.cfin-coins__mark .mdi {
  font-size: inherit;
}
.cfin-coins__value {
  margin: 8px 0 0;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}
.cfin-coins__hint {
  margin: 10px 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85);
  max-width: 22ch;
}

/* ---------- livro de mensalidades ---------- */
.cfin-ledger {
  margin-top: 26px;
  padding: 24px 24px 26px;
  border-radius: 22px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.cfin-ledger__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.cfin-section {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.cfin-section__sub {
  margin: 3px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
}
.cfin-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
}
.cfin-legend__i {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: var(--muted);
}
.cfin-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c);
}

.cfin-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
}
.cfin-row {
  --c: var(--dk-ember, #ff5d2a);
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 15px 18px 15px 20px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.cfin-row:hover {
  border-color: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.16);
  box-shadow: 0 14px 30px -24px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.6);
}
.cfin-row__stripe {
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: var(--c);
}
.cfin-row__info {
  min-width: 0;
  flex: 1 1 auto;
}
.cfin-row__title {
  margin: 0;
  font-size: 0.96rem;
  font-weight: 700;
  color: var(--ink);
}
.cfin-row__meta {
  margin: 3px 0 0;
  overflow-wrap: anywhere;
  font-size: 0.8rem;
  color: var(--muted);
}
.cfin-row__right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: none;
}
.cfin-row__amount {
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.cfin-row__coins {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--dk-teal-deep, #00805f);
}
.cfin-status {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c);
  background: color-mix(in srgb, var(--c) 12%, var(--dk-surface, #fff));
  white-space: nowrap;
}

.cfin-empty {
  margin-top: 18px;
  padding: 28px 20px;
  border: 1px dashed var(--line);
  border-radius: 16px;
  text-align: center;
  font-size: 0.9rem;
  color: var(--muted);
}

/* ---------- responsivo (pela largura do conteudo) ---------- */

/* Sem espaco para tres colunas: o valor a pagar ocupa a linha inteira e
   mensalidade e Pitch Coins dividem a linha de baixo (ou a ocupam sozinhos,
   quando so um dos dois existe). */
@container cfin (max-width: 1000px) {
  .cfin-hero {
    display: flex;
    flex-wrap: wrap;
  }
  .cfin-hero__charge {
    flex: 1 1 100%;
  }
  .cfin-plan,
  .cfin-coins {
    flex: 1 1 240px;
    min-width: 0;
  }
}

@container cfin (max-width: 600px) {
  .cfin-hero {
    gap: 12px;
  }
  .cfin-hero__charge {
    padding: 20px 18px;
    border-radius: 20px;
  }
  .cfin-plan,
  .cfin-coins {
    flex-basis: 100%;
    padding: 18px;
  }
  .cfin-coins__value {
    font-size: 1.7rem;
  }
  .cfin-ledger {
    margin-top: 18px;
    padding: 18px 14px 20px;
    border-radius: 20px;
  }
  .cfin-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 14px 14px 18px;
  }
  .cfin-row__right {
    justify-content: space-between;
  }
}

/* Navegador sem container query: o comportamento antigo, pela tela. */
@supports not (container-type: inline-size) {
  @media (max-width: 1260px) {
    .cfin-hero {
      display: flex;
      flex-wrap: wrap;
    }
    .cfin-hero__charge {
      flex: 1 1 100%;
    }
    .cfin-plan,
    .cfin-coins {
      flex: 1 1 240px;
      min-width: 0;
    }
  }
  @media (max-width: 600px) {
    .cfin-row {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }
    .cfin-row__right {
      justify-content: space-between;
    }
  }
}
</style>
